// Where unfinished forms live: one IndexedDB store of records keyed by form ("newTell",
// "edit:<postId>", "place:new"), photos included as Blobs, so a reload, a camera round-trip or
// the installed app being closed loses nothing. A small index of the listed records sits in
// localStorage so the draft banner can render synchronously before IndexedDB answers.
// Without IndexedDB (private mode, an old browser) the fields still go to localStorage and only
// photos are lost, which is the state the app was in before this existed.

export interface StoredPhoto {
    id: string;
    blob: Blob;
    name: string;
    type: string;
}

export interface StoredDraft {
    key: string;
    /** Shown in the banner: "New tell", "Unsaved changes to 'Six seedlings in'". */
    label: string;
    /** Where Continue goes. */
    route: string;
    /** Listed in the banner? Small sheets keep a draft but do not nag. */
    listed: boolean;
    fields: Record<string, unknown>;
    photos: StoredPhoto[];
    startedAt: string;
    updatedAt: string;
}

export interface DraftIndexEntry {
    key: string;
    label: string;
    route: string;
    updatedAt: string;
    photoCount: number;
}

const DB_NAME = 'sowandtell';
const STORE = 'drafts';
const INDEX_KEY = 'draftIndex';
const FALLBACK_PREFIX = 'draft:';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(resolve => {
        try {
            if (typeof indexedDB === 'undefined') {
                resolve(null);
                return;
            }
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => request.result.createObjectStore(STORE, {keyPath: 'key'});
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
            request.onblocked = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
    return dbPromise;
}

const run = <T>(db: IDBDatabase, mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = op(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

// ---------------------------------------------------------------- the index

const listeners = new Set<() => void>();
let index: DraftIndexEntry[] = readIndex();

function readIndex(): DraftIndexEntry[] {
    try {
        const raw = window.localStorage.getItem(INDEX_KEY);
        return raw ? (JSON.parse(raw) as DraftIndexEntry[]) : [];
    } catch {
        return [];
    }
}

function writeIndex(next: DraftIndexEntry[]) {
    index = next;
    try {
        if (next.length === 0) window.localStorage.removeItem(INDEX_KEY);
        else window.localStorage.setItem(INDEX_KEY, JSON.stringify(next));
    } catch {
        // ignore
    }
    listeners.forEach(l => l());
}

const upsertIndex = (record: StoredDraft) => {
    const entry: DraftIndexEntry = {key: record.key, label: record.label, route: record.route, updatedAt: record.updatedAt, photoCount: record.photos.length};
    const rest = index.filter(e => e.key !== record.key);
    writeIndex(record.listed ? [...rest, entry].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)) : rest);
};

const dropIndex = (key: string) => {
    if (index.some(e => e.key === key)) writeIndex(index.filter(e => e.key !== key));
};

// ---------------------------------------------------------------- the API

export const draftStorage = {
    async save(record: StoredDraft): Promise<void> {
        upsertIndex(record);
        const db = await openDb();
        if (db) {
            try {
                await run(db, 'readwrite', store => store.put(record));
                return;
            } catch {
                // fall through to localStorage for the fields
            }
        }
        try {
            const {photos: _photos, ...rest} = record;
            window.localStorage.setItem(FALLBACK_PREFIX + record.key, JSON.stringify({...rest, photos: []}));
        } catch {
            // nothing more to try
        }
    },

    async load(key: string): Promise<StoredDraft | null> {
        const db = await openDb();
        if (db) {
            try {
                const record = (await run<StoredDraft | undefined>(db, 'readonly', store => store.get(key))) ?? null;
                if (record) return record;
            } catch {
                // fall through
            }
        }
        try {
            const raw = window.localStorage.getItem(FALLBACK_PREFIX + key);
            return raw ? (JSON.parse(raw) as StoredDraft) : null;
        } catch {
            return null;
        }
    },

    async remove(key: string): Promise<void> {
        dropIndex(key);
        try {
            window.localStorage.removeItem(FALLBACK_PREFIX + key);
        } catch {
            // ignore
        }
        const db = await openDb();
        if (db) {
            try {
                await run(db, 'readwrite', store => store.delete(key));
            } catch {
                // ignore
            }
        }
    },

    /** The listed drafts, synchronously, newest first. */
    index: () => index,

    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }
};
